import React, { useState } from "react";
import { CarFront, Check, ChevronsUpDown, Truck } from "lucide-react";
import { Form, FormControl, FormField, FormItem, FormLabel, FormMessage } from "@/components/ui/form";
import { Label } from "@/components/ui/label";
import { Select, SelectContent, SelectItem, SelectTrigger, SelectValue } from "@/components/ui/select";
import { ButtonGroup, ButtonGroupItem } from "@/components/CustomRadioGroup";
import { Input } from "@/components/ui/input";
import { Button } from "@/components/ui/button";
import { Command, CommandEmpty, CommandGroup, CommandInput, CommandItem, CommandList } from "@/components/ui/command";
import { Dialog, DialogContent, DialogHeader, DialogTitle } from "@/components/ui/dialog";
import { cn } from "@/lib/utils";

interface StepDettagliGeneraliProps {
    formDettagliGenerali: any;
    step: number;
    veichleType: "auto" | "vcom";
    setVeichleType: (val: "auto" | "vcom") => void;
    marche: any[];
    anni: string[];
    marca: string;
    mesi: { value: string; label: string }[];
    anno: string;
    modelli: any[];
    mese: string;
    versioni: any[];
    modello: string;
    versione: string;
    chilometri: string;
    permuta: string;
    hidePermutaChoice?: boolean;
}

function SearchableSelect({
    value,
    placeholder,
    searchPlaceholder,
    disabled,
    options,
    onChange,
}: {
    value: string;
    placeholder: string;
    searchPlaceholder: string;
    disabled?: boolean;
    options: Array<{ value: string; label: string }>;
    onChange: (value: string) => void;
}) {
    const [open, setOpen] = useState(false);
    const selectedOption = options.find((option) => option.value === value);

    return (
        <div className="relative">
            <Button
                type="button"
                variant="outline"
                role="combobox"
                aria-expanded={open}
                disabled={disabled}
                onClick={() => setOpen((prev) => !prev)}
                className="w-full justify-between font-normal"
            >
                <span className="truncate">{selectedOption?.label || placeholder}</span>
                <ChevronsUpDown className="ml-2 h-4 w-4 shrink-0 opacity-50" />
            </Button>
            <Dialog open={open} onOpenChange={setOpen}>
                <DialogContent className="max-h-[80vh] overflow-hidden p-0 sm:max-w-lg">
                    <DialogHeader className="px-4 pt-4">
                        <DialogTitle>{placeholder}</DialogTitle>
                    </DialogHeader>
                    <Command>
                        <CommandInput placeholder={searchPlaceholder} />
                        <CommandList className="max-h-[60vh] overscroll-contain">
                            <CommandEmpty>Nessun risultato.</CommandEmpty>
                            <CommandGroup>
                                {options.map((option) => (
                                    <CommandItem
                                        key={option.value}
                                        value={option.label}
                                        onSelect={() => {
                                            onChange(option.value);
                                            setOpen(false);
                                        }}
                                        onClick={() => {
                                            onChange(option.value);
                                            setOpen(false);
                                        }}
                                    >
                                        <Check className={cn("mr-2 h-4 w-4", value === option.value ? "opacity-100" : "opacity-0")} />
                                        {option.label}
                                    </CommandItem>
                                ))}
                            </CommandGroup>
                        </CommandList>
                    </Command>
                </DialogContent>
            </Dialog>
        </div>
    );
}

const StepDettagliGenerali: React.FC<StepDettagliGeneraliProps> = ({
    formDettagliGenerali,
    step,
    veichleType,
    setVeichleType,
    marche,
    anni,
    marca,
    mesi,
    anno,
    modelli,
    mese,
    versioni,
    modello,
    versione,
    chilometri,
    permuta,
    hidePermutaChoice = false,
}) => {
    const formatProductionEndDate = (value?: string | null) => {
        if (!value) return null;
        const [year, month, day] = value.split("-");
        if (!year || !month || !day) return null;
        return `${day}/${month}/${year}`;
    };

    const marcaOptions = marche.map((item, index) => ({
        value: item.acronimo ?? `marca-${index}`,
        label: item.nome ?? "Marca",
    }));

    const modelloOptions = modelli.map((item, index) => ({
        value: item.gammaModello?.codice ?? `modello-${index}`,
        label: item.gammaModello?.descrizione ?? "Modello",
    }));

    const versionNameOccurrences = versioni.reduce<Record<string, number>>((acc, item) => {
        const name = String(item?.nome ?? "").trim();
        if (!name) return acc;
        acc[name] = (acc[name] ?? 0) + 1;
        return acc;
    }, {});

    const versioneOptions = versioni.map((item, index) => {
        const nome = String(item?.nome ?? "Versione").trim() || "Versione";
        const isDuplicated = (versionNameOccurrences[nome] ?? 0) > 1;
        const fineProduzione = formatProductionEndDate(item?.fineProduzione);

        return {
            value: String(item?.codiceMotornetUnivoco ?? `versione-${index}`),
            label: isDuplicated && fineProduzione
                ? `${nome} fine produzione ${fineProduzione}`
                : nome,
        };
    });

    return (
        <Form {...formDettagliGenerali}>
            <form
                id="formDettagliGenerali"
                onSubmit={formDettagliGenerali.handleSubmit(() => {})}
                className="flex flex-col gap-4"
            >
                <div className="space-y-2">
                    <Label>Tipo di veicolo</Label>
                    <div className="grid w-full grid-cols-2 gap-3">
                        <Button
                            type="button"
                            variant={veichleType === "auto" ? "default" : "outline"}
                            onClick={() => setVeichleType("auto")}
                            className={cn(
                                "flex items-center justify-center gap-2",
                                veichleType === "auto" && "bg-primary text-white hover:bg-primary/90 hover:text-white"
                            )}
                        >
                            <CarFront className="h-4 w-4" />
                            Auto
                        </Button>
                        <Button
                            type="button"
                            variant={veichleType === "vcom" ? "default" : "outline"}
                            onClick={() => setVeichleType("vcom")}
                            className={cn(
                                "flex items-center justify-center gap-2",
                                veichleType === "vcom" && "bg-primary text-white hover:bg-primary/90 hover:text-white"
                            )}
                        >
                            <Truck className="h-4 w-4" />
                            Commerciali
                        </Button>
                    </div>
                </div>

                {step >= 1 && (
                    <FormField
                        control={formDettagliGenerali.control}
                        name="marca"
                        render={({ field, fieldState }) => (
                            <FormItem>
                                <FormLabel>Di che marca è la tua auto?</FormLabel>
                                <FormControl>
                                    <SearchableSelect
                                        value={field.value}
                                        placeholder="Seleziona la marca"
                                        searchPlaceholder="Cerca marca..."
                                        options={marcaOptions}
                                        onChange={field.onChange}
                                    />
                                </FormControl>
                                <FormMessage>{fieldState.error?.message}</FormMessage>
                            </FormItem>
                        )}
                    />
                )}

                {step >= 1 && (
                    <FormField
                        control={formDettagliGenerali.control}
                        name="anno"
                        render={({ field, fieldState }) => (
                            <FormItem>
                                <FormLabel>In che anno è stata immatricolata?</FormLabel>
                                <FormControl>
                                    <Select {...field} onValueChange={field.onChange} value={field.value} disabled={!marca}>
                                        <SelectTrigger>
                                            <SelectValue placeholder="Seleziona anno" />
                                        </SelectTrigger>
                                        <SelectContent>
                                            {anni.map((annoItem) => (
                                                <SelectItem key={annoItem} value={annoItem}>
                                                    {annoItem}
                                                </SelectItem>
                                            ))}
                                        </SelectContent>
                                    </Select>
                                </FormControl>
                                <FormMessage>{fieldState.error?.message}</FormMessage>
                            </FormItem>
                        )}
                    />
                )}

                {step >= 1 && (
                    <FormField
                        control={formDettagliGenerali.control}
                        name="mese"
                        render={({ field, fieldState }) => (
                            <FormItem>
                                <FormLabel>In che mese è stata immatricolata?</FormLabel>
                                <FormControl>
                                    <Select {...field} onValueChange={field.onChange} value={field.value} disabled={!anno}>
                                        <SelectTrigger>
                                            <SelectValue placeholder="Seleziona mese" />
                                        </SelectTrigger>
                                        <SelectContent>
                                            {mesi.map((meseItem, index) => (
                                                <SelectItem key={index} value={meseItem.value}>
                                                    {meseItem.label}
                                                </SelectItem>
                                            ))}
                                        </SelectContent>
                                    </Select>
                                </FormControl>
                                <FormMessage>{fieldState.error?.message}</FormMessage>
                            </FormItem>
                        )}
                    />
                )}

                {step >= 3 && (
                    <FormField
                        control={formDettagliGenerali.control}
                        name="modello"
                        render={({ field, fieldState }) => (
                            <FormItem>
                                <FormLabel>Qual è il modello?</FormLabel>
                                <FormControl>
                                    <SearchableSelect
                                        value={field.value}
                                        placeholder="Seleziona modello"
                                        searchPlaceholder="Cerca modello..."
                                        disabled={!mese}
                                        options={modelloOptions}
                                        onChange={field.onChange}
                                    />
                                </FormControl>
                                <FormMessage>{fieldState.error?.message}</FormMessage>
                            </FormItem>
                        )}
                    />
                )}

                {step >= 4 && (
                    <FormField
                        control={formDettagliGenerali.control}
                        name="versione"
                        render={({ field, fieldState }) => (
                            <FormItem>
                                <FormLabel>Qual è la versione?</FormLabel>
                                <FormControl>
                                    <SearchableSelect
                                        value={field.value}
                                        placeholder="Seleziona versione"
                                        searchPlaceholder="Cerca versione..."
                                        disabled={!modello}
                                        options={versioneOptions}
                                        onChange={field.onChange}
                                    />
                                </FormControl>
                                <FormMessage>{fieldState.error?.message}</FormMessage>
                            </FormItem>
                        )}
                    />
                )}

                {step >= 5 && (
                    <FormField
                        control={formDettagliGenerali.control}
                        name="chilometri"
                        render={({ field, fieldState }) => (
                            <FormItem>
                                <FormLabel>Quanti chilometri hai percorso</FormLabel>
                                <FormControl>
                                    <Input {...field} placeholder="Inserisci i chilometri" disabled={!versione} />
                                </FormControl>
                                <FormMessage>{fieldState.error?.message}</FormMessage>
                            </FormItem>
                        )}
                    />
                )}

                {step >= 6 && !hidePermutaChoice && (
                    <FormField
                        control={formDettagliGenerali.control}
                        name="permuta"
                        render={({ field, fieldState }) => (
                            <FormItem>
                                <FormLabel>Sei interessato alla permuta?</FormLabel>
                                <FormControl>
                                    <ButtonGroup
                                        ref={field.ref}
                                        value={field.value}
                                        onValueChange={field.onChange}
                                        disabled={!chilometri}
                                    >
                                        <ButtonGroupItem
                                            className="w-full justify-between px-3"
                                            value="Permuta"
                                            label="Si, voglio permutare la mia auto"
                                        />
                                        <ButtonGroupItem
                                            className="w-full justify-between px-3"
                                            value="Vendere"
                                            label="No, voglio solo vendere la mia auto"
                                        />
                                    </ButtonGroup>
                                </FormControl>
                                <FormMessage>{fieldState.error?.message}</FormMessage>
                            </FormItem>
                        )}
                    />
                )}

                {step >= 7 && (
                    <FormField
                        control={formDettagliGenerali.control}
                        name="email"
                        render={({ field, fieldState }) => (
                            <FormItem>
                                <FormLabel>E-mail</FormLabel>
                                <FormControl>
                                    <Input
                                        {...field}
                                        type="email"
                                        placeholder="Inserisci la tua e-mail"
                                        disabled={!permuta}
                                    />
                                </FormControl>
                                <FormMessage>{fieldState.error?.message}</FormMessage>
                            </FormItem>
                        )}
                    />
                )}
            </form>
        </Form>
    );
};

export default StepDettagliGenerali;
